import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { SecureStorageService } from '../../../core/services/secure-storage.service';
import { ContestRequestModel } from '../models/contest-request-model';
import { ContestEditModel } from '../models/contest-edit-model';
import { ContestApproveRejectModel } from '../models/contest-approve-reject-model';

@Injectable({
  providedIn: 'root',
})
export class ContestsService {
  constructor(
    private http: HttpClient,
    // The gateway takes the session token on the Authorization header, and it is held in
    // secure storage — not localStorage — so the header cannot be built without this.
    private secureStorage: SecureStorageService
  ) { }

  // ── gateway (microservices) ─────────────────────────────────────────────────
  // The 17 codes wired on this service (8136-8143, 8148-8152, 8153, 8156, 8158-8160, 8163,
  // 8171) all go through callAction() below. The url is resolved per-browser by
  // resolveGatewayOrigin() in environment.ts, so a shared link carries its own gateway.
  private readonly gatewayUrl = environment.loyaltyApiUrl;

  private gatewayHeaders(skipLoader = false): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.secureStorage.getItem('authToken') || ''}`,
    });
    if (skipLoader) headers = headers.set('X-Skip-Loader', 'true');
    return headers;
  }

  requestContest(contest: any): Observable<any> {
    return this.callAction<any>(8138, contest).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  saveAsDraft(contest: any): Observable<any> {
    return this.callAction<any>(8138, { ...contest, status: 'draft' }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  // 8293 · copy a contest inside the database → { id, contest_id, name, status, dates_shifted }
  duplicateContest(contestId: string): Observable<any> {
    return this.callAction<any>(8293, { contest_id: contestId });
  }

  deleteLevel(levelId: number): Observable<any> {
    return this.callAction<any>(8141, { id: levelId });
  }

  // The per-level completion badges used to fetch the same legacy endpoint a second time. They
  // are now filled from the single 8183 response in loadLevelMatrix, so this asked legacy for
  // data the screen already had — and got the legacy empty participation for it.
  getLevelCompletionMetrics(contestId: string): Observable<any> {
    return this.callAction<any>(8183, { contest_id: contestId }, true).pipe(
      catchError(() => throwError(() => new Error('Failed to load level completion metrics.')))
    );
  }

  // The Level Matrix funnel. It was asking the legacy contest_level_completion_metrics, which charts
  // the legacy participation — and every participation now lives in the redesigned database, so the
  // table was empty for every contest no matter how much had been played. 8183 runs the same
  // query the client panel's own funnel uses, so the two cannot show different numbers.
  getLevelMatrix(contestId: string, skipLoader = false): Observable<any> {
    return this.callAction<any>(8183, { contest_id: contestId }, skipLoader).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.error?.message || 'Failed to load level matrix.'))
      )
    );
  }

  deletePrize(prizeId: number): Observable<any> {
    return this.callAction<any>(8142, { id: prizeId });
  }

  // ── the winner draw ─────────────────────────────────────────────────────────
  // There was no way to run a draw from here at all. legacy drew winners from a cron that looked
  // only at the last thirty minutes, so a contest whose announcement passed while that job was
  // down never got its winners and nobody could ask for them. The scheduled job still does the
  // routine work; these two are the handle on it.
  //
  // 8182 is safe to press twice: the draw records itself, and a second press reports that the
  // contest is already drawn rather than awarding a second set of prizes.
  runContestDraw(contestId: string): Observable<any> {
    return this.callAction<any>(8182, { contest_id: contestId }).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to run the draw.'))
      )
    );
  }

  /** What the draw decided, and the rules it decided under. */
  getContestDraw(contestId: string): Observable<any> {
    return this.callAction<any>(8184, { contest_id: contestId }, true).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load the draw.'))
      )
    );
  }

  editContest(contestId: string, contestData: ContestEditModel) {
    return this.callAction<any>(8138, { ...contestData, id: contestId, contest_id: contestId }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  getContestById(contestId: string): Observable<any> {
    return this.callAction<any>(8137, { id: contestId }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  toggleElectionStatus(contestId: string, isEnabled: boolean): Observable<any> {
    return this.callAction<any>(8150, { contest_id: contestId, seed: isEnabled }).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error?.message || 'Failed to update the draw status. Please try again.')
        );
      })
    );
  }

  // 8150, the same action toggleElectionStatus() uses — these two were always one operation
  // and this one never moved off legacy. It posted to a raw legacy URL, took the 404, then "fell
  // back" to a second legacy URL that answers 400, so turning the draw on or off from the wizard
  // failed silently apart from a red line in the Network tab.
  //
  // 8150 reads `seed` (or is_enabled) and writes contest_draw_configs.is_enabled. `reason` is
  // kept in the payload because the confirm dialogs collect one; the action ignores it for now
  // rather than the caller pretending it was never asked for.
  toggleDraw(contestId: string, isDrawEnabled: boolean, reason: string = ''): Observable<any> {
    return this.callAction<any>(8150, {
      contest_id: contestId,
      seed: isDrawEnabled,
      is_draw_enabled: isDrawEnabled,
      reason: reason || ''
    }).pipe(
      catchError((error) => throwError(
        () => new Error(error?.error?.message || error?.message || 'Failed to update draw status.')
      ))
    );
  }

  approveRejectContets(
    approveContestData: ContestApproveRejectModel
  ): Observable<any> {
    return this.callAction<any>(8140, approveContestData as any).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to approve campaign. Please try again.')))
    );
  }

  /** action 8117 · admin-contest-publish — the one action behind all four destinations.
   *
   *  These were three separate legacy endpoints (admin_publish_sdk, luqta_sdk, admin_publish_web)
   *  and an "All" that quietly saved Election Rules instead. None of them reached
   *  contest_publications, and none checked whether the contest's BUSINESS is entitled to the
   *  surface being published to.
   *
   *  8117 checks the owning business's plan — SDK needs the SDK, Web needs Web access, All needs
   *  both, LuQta is the phone app and is the admin's to grant — and refuses by name when it is
   *  not there, so the message the admin sees says whose plan is missing what. */
  publishContest(contestId: string, destination: 'sdk' | 'both' | 'web' | 'all' | 'luqta'):Observable<any> {
    return this.callAction<any>(8117, { contest_id: contestId, destination }).pipe(
      catchError((error) => throwError(
        () => new Error(error?.error?.message || error?.message || 'Failed to publish the engagement.')
      ))
    );
  }

  admin_publish_sdk(contestId: string, _isWebApproval?: boolean): Observable<any> {
    return this.publishContest(contestId, 'sdk');
  }

  admin_publish_web(contestId: string, _isWebApproval?: boolean): Observable<any> {
    return this.publishContest(contestId, 'web');
  }

  admin_publish_luqta_sdk(contestId: string): Observable<any> {
    return this.publishContest(contestId, 'both');
  }

  // TODO(backend): confirm 8117 accepts destination 'luqta' (LuQta-only was added on main
  // after the microservices migration; it previously hit legacy admin_publish_luqta).
  admin_publish_luqta(contestId: string): Observable<any> {
    return this.publishContest(contestId, 'luqta');
  }

  rejectContest(contestId: string, reason: string): Observable<any> {
    return this.callAction<any>(8140, { contest_id: contestId, action: 'reject', reason }).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to reject campaign. Please try again.')))
    );
  }

  getRequestContest(skipLoader = false): Observable<ContestRequestModel[]> {
    return this.callAction<any>(8136, {}, skipLoader).pipe(
      map((res: any) => (res?.items ?? res) as ContestRequestModel[]),
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load campaign request.')))
    );
  }

  getRequestContestPaginated(
    params: any,
    skipLoader = false
  ): Observable<ContestRequestModel[]> {
    return this.callAction<any>(8136, params ?? {}, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load campaign request.')))
    );
  }

  /** action 8245 · admin-client-branch-list — a company's branches (uuid client id). Replaces
   *  the old branch lookup. */
  getBranchesByClient(clientId: number | string): Observable<any> {
    return this.callAction<any>(8245, { client_id: String(clientId) }, true).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load branches by client.'))
      )
    );
  }

  getSdkContests(params: any, skipLoader = false): Observable<any> {
    const toNumberOrZero = (value: any) => {
      const n = Number(value);
      return isNaN(n) ? 0 : n;
    };

    const payload: Record<string, any> = {
      status: params?.status ?? '',
      format: params?.format ?? '',
      type: params?.type ?? '',
      view_type: params?.view_type ?? '',
      contest_category: params?.contest_category ?? '',
      country: params?.country ?? '',
      search: params?.search ?? '',
      page: toNumberOrZero(params?.page),
      per_page: toNumberOrZero(params?.per_page),
      expiry: params?.expiry ?? '',
      draw_type: params?.draw_type ?? '',
      gender: params?.gender ?? '',
    };

    const companyId = params?.company_id;
    if (companyId !== undefined && companyId !== null && String(companyId).trim() !== '') {
      payload['company_id'] = toNumberOrZero(companyId);
    }

    return this.callAction<any>(8152, payload, skipLoader).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load SDK campaigns.'))
      )
    );
  }

  exportAllContests(): Observable<any> {
    return this.callAction<any>(8136, { per_page: 100000 })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(
                error?.message || 'Failed to export campaigns.'
              )
          );
        })
      );
  }

  /** action 8136 · admin-contest-list — the contest monitoring grid (paged envelope with `items`). */
  getContestMonitoringPaginated(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8136, params ?? {}, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load campaign request.')))
    );
  }

  // Contest requests - server-side search/pagination
  getContestRequestsList(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8163, params ?? {}, skipLoader).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load campaign requests.')))
    );
  }

  /** action 8164 · admin-contest-lead-update-status. */
  updateContestRequestStatus(payload: {
    id: number | string;
    status: string;
    /** Sent with status 'rejected': the reason shown to the client. */
    rejection_reason?: string;
  }): Observable<any> {
    // request_id mirrors id so the action finds the row whichever key it reads.
    return this.callAction<any>(8164, { ...payload, request_id: payload.id }).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to update campaign request status.')))
    );
  }

  /**
   * action 8179 · admin-participant-list — the whole list, unpaged.
   *
   * The paginated method below is what the grid uses; this one is for the callers that want
   * every row at once (the contest detail page, the export builders), so it asks for one very
   * large page and hands back the items array they expect.
   */
  getParticipateDetails(contest_id: string, skipLoader = false): Observable<any[]> {
    return this.callAction<any>(8179, { contest_id, page: 1, per_page: 10000 }, skipLoader).pipe(
      map((res) => (Array.isArray(res?.items) ? res.items : [])),
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load participant data.'))
      )
    );
  }

  /**
   * action 8179 · admin-participant-list — the grid, paged server-side.
   *
   * `contest_id` is a uuid. While this called legacy it was sent as one and legacy answered
   * "Value is not a valid integer", so the grid was empty for every contest — nothing was
   * wrong with the data.
   *
   * `status` filters on the three derived states: viewer / in_progress / completed. A viewer
   * is someone who opened the contest and has not finished a level; the grid already has a
   * colour for it.
   */
  getParticipateDetailsPaginated(params: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8179, params ?? {}, skipLoader).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load participant data.'))
      )
    );
  }

  /**
   * action 8180 · admin-participant-progress — every player's per-level answers.
   *
   * A flat array, which is what the Answer column filters in memory by user_id.
   */
  getUserProgress(contest_id: string): Observable<any[]> {
    return this.callAction<any[]>(8180, { contest_id }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load user progress.'))
      )
    );
  }

  /**
   * action 8180 · admin-participant-progress — the same read as getUserProgress().
   *
   * Two names for one call because two screens ask for it under two different labels; kept
   * separate so neither caller has to change, but they resolve to the same action.
   */
  getTextLevelAnswers(contest_id: string): Observable<any[]> {
    return this.callAction<any[]>(8180, { contest_id }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load level answers.'))
      )
    );
  }

  getDrawHistoryPaginated(
    params: any,
    skipLoader = false
  ): Observable<ContestRequestModel[]> {
    return this.callAction<any>(8151, params ?? {}, skipLoader).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load draw history.'))
      )
    );
  }

  deleteContest(contestId: string): Observable<void> {
    return this.callAction<any>(8139, { id: contestId })
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error?.message || 'Failed to delete campaign.')
          );
        })
      );
  }

  /** 8266 — un-archive a contest 8139 archived. */
  restoreContest(contestId: string): Observable<any> {
    return this.callAction<any>(8266, { id: contestId })
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error?.message || 'Failed to restore campaign.')
          );
        })
      );
  }

  addElectionRules(contest: any): Observable<any> {
    return this.callAction<any>(8149, contest)
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error?.message || 'Failed to save election rules. Please try again.')
          );
        })
      );
  }

  getElectionRulesData(contest: any): Observable<any> {
    return this.callAction<any>(8149, contest)
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error?.message || 'Failed to save election rules. Please try again.')
          );
        })
      );
  }

  getElectionRulesByContest(contestId: string): Observable<any> {
    return this.callAction<any>(8148, { contest_id: contestId })
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error?.message || 'Failed to load election rules.')
          );
        })
      );
  }

  /** Fetches each client's web-access eligibility for the Draw Rules screen —
   *  called with every client the contest is linked to so the admin can see,
   *  per client, whether Web Publish will actually apply before publishing.
   *  action 8235 · admin-contest-clients-web-access → { clients: [...] }. The old raw
   *  legacy endpoint rejected the gateway admin token with 401. */
  getClientsWebAccess(clientIds: Array<string | number>): Observable<any> {
    return this.callAction<any>(8235, { client_ids: clientIds })
      .pipe(
        catchError((error) => {
          return throwError(
            () => new Error(error?.error?.message || error?.message || 'Failed to load client web access.')
          );
        })
      );
  }

  saveImage(imageData: any): Observable<any> {
    return this.callAction<any>(8143, imageData).pipe(
      catchError((error) => {
        return throwError(
          () => new Error(error?.message || 'Failed to upload image. Please try again.')
        );
      })
    );
  }

  generateAiImage(prompt: string): Observable<any> {
    const chatboltUrl =
      (environment as any).aiChatboltBaseUrl ||
      'https://xtro-edge-chatbolt-backend.hf.space';
    const url = `${chatboltUrl}/api/v1/ai-endpoints/public/execute-by-name/image_regenerator-l9o6p`;
    const headers = {
      'x-api-key': (environment as any).chatboltEndpointsKey,
      'Content-Type': 'application/json',
      'X-Skip-Loader': 'true',
    };
    const enhancedPrompt =
      prompt.trim() +
      ' Beautiful golden hour lighting, vibrant rich colors, professional advertising campaign style, ultra high quality, 8k, photorealistic, shallow depth of field, absolutely no text no letters no words no numbers no watermark no overlay no typography';
    return this.http
      .post(url, { image_prompt: enhancedPrompt }, { headers })
      .pipe(
        catchError((error) =>
          throwError(
            () => new Error(
              error?.error?.message ||
              error?.error?.error ||
              error?.message ||
              'AI image generation failed. Please try again.'
            )
          )
        )
      );
  }

  /** action 8140 · admin-contest-approve-reject — the grid's Active / Inactive switch.
   *  It only flips is_active ('activate' / 'deactivate'), for any contest — expired and completed
   *  ones included — so the admin always decides whether the app shows it. A contest the old grid
   *  switched off by rejecting it is switched back on by approving it, as before. */
  updateUserStatus(data: { contest_id: string; status: string; review_status?: string }) {
    const activate = String(data.status).toLowerCase() === 'true';
    const wasRejected = String(data.review_status || '').toLowerCase() === 'rejected';
    return this.callAction<any>(8140, {
      contest_id: data.contest_id,
      action: activate && wasRejected ? 'approve' : activate ? 'activate' : 'deactivate',
    });
  }

  // The master prize catalogue (reusable prize templates) has no table on this platform yet, so these
  // five methods fail with a clear message. A contest's own prizes are saved with the contest (8138).
  addPrize(prizeData: any): Observable<any> {
    return this.prizeCatalogueUnavailable();
  }

  getWinnersPaginated(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8153, params ?? {}, skipLoader).pipe(
      catchError(() => throwError(() => new Error('Failed to get winners. Please try again.')))
    );
  }

  getPrize(): Observable<any> {
    return this.prizeCatalogueUnavailable();
  }

  getPrizeById(prizeId: number | string): Observable<any> {
    return this.prizeCatalogueUnavailable();
  }

  updatePrize(prizeId: number | string, prizeData: any): Observable<any> {
    return this.prizeCatalogueUnavailable();
  }

  deleteMasterPrize(prizeId: number | string): Observable<any> {
    return this.prizeCatalogueUnavailable();
  }

  private prizeCatalogueUnavailable(): Observable<any> {
    return throwError(() => new Error('The prize catalogue is not available on this platform yet.'));
  }

  generateLink(contestId: any): string {
    return `https://luqta.io/detailContestPage?contestId=${contestId}`;
  }

  getCountries(skipLoader = false): Observable<any[]> {
    return this.callAction<any[]>(8171, {}, skipLoader).pipe(
      catchError(() => throwError(
        () => new Error('Failed to load countries. Please try again.')
      )),
    );
  }

  /** action 8289 · admin-participant-disqualify — records an active fraud flag on this contest for
   *  the participant (and disqualifies them if they are a winner). */
  disqualifyParticipant(data: { contest_id: string; participant_id: string }): Observable<any> {
    return this.callAction<any>(8289, data).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to disqualify participant.'))
      )
    );
  }

  // ─── Survey Responses ────────────────────────────────────────────────────────

  getSurveyResponses(params: {
    contest_id: string;
    from_date?: string | null;
    to_date?: string | null;
    search?: string;
    page?: number;
    per_page?: number;
  }): Observable<any> {
    // action 8210 · admin-survey-responses. The response visibility set for this business (or
    // overridden on the level) is applied server-side, so an aggregated survey comes back as
    // counts with no rows — see AdminSurvey.service.ts.
    return this.callAction<any>(8210, params ?? {}).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load survey responses.'))
      )
    );
  }

  /** Per-participant survey answers for a single survey level. Unlike
   *  getSurveyResponses (contest-wide grid), this returns the question →
   *  answer pairs one end user submitted on one level. */
  getSurveyAnswersDetail(params: {
    contest_id: string;
    // uuids, not numbers — every id here is one, and typing them as numbers is what invites the
    // Number() conversions that turn a uuid into NaN.
    level_id?: string;
    end_user_id?: string;
  }): Observable<any> {
    /** action 8211 · admin-survey-answer-detail. */
    return this.callAction<any>(8211, params ?? {}).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load survey answers.'))
      )
    );
  }

  // ─── Winner Override & Audit Log ─────────────────────────────────────────────

  /** action 8157 · admin-winner-override-audit-log. */
  getWinnerOverrideAuditLog(params: {
    status?: string;
    gender?: string;
    country?: string;
    page?: number;
    per_page?: number;
  }): Observable<any> {
    return this.callAction<any>(8157, params ?? {}).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load audit log.')))
    );
  }

  /** action 8155 · admin-winner-set. */
  setContestWinner(data: { contest_id: string; participant_id: string; reason: string }): Observable<any> {
    return this.callAction<any>(8155, data).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to set winner.')))
    );
  }

  /** action 8154 · admin-prize-award-upsert. Collapses the old create-then-update pair
   *  (createPrizeHistory + updatePrizeStatus) into one call — the backend decides whether
   *  a row exists, which the component could only guess at from whatever prize_history the
   *  winners response happened to embed. */
  // tracking_number is optional and only sent for the three shipping statuses, which the
  // schema will not accept without one (prize_awards_shipped_has_tracking). A spread bypasses
  // the excess-property check, so the type is widened rather than left quietly wrong.
  upsertPrizeAward(body: {
    contest_prize_id: string | number;
    end_user_id: any;
    status: string;
    tracking_number?: string;
    courier_name?: string;
  }): Observable<any> {
    return this.callAction<any>(8154, body).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to update prize status. Please try again.')))
    );
  }

  getContestParticipants(contestId: string): Observable<any> {
    return this.callAction<any>(8156, { contest_id: contestId }).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load participants.')))
    );
  }

  // ─── Top Points Leaderboard ──────────────────────────────────────────────────

  /** action 8290 · admin-contest-leaderboard — { items: [{ display_name, full_name, points_collected, country }], itemsTotal }. */
  getContestLeaderboard(params: {
    contest_id: string;
    page?: number;
    per_page?: number;
  }): Observable<any> {
    return this.callAction<any>(8290, params).pipe(
      catchError((error) =>
        throwError(() => new Error(error?.message || 'Failed to load leaderboard.'))
      )
    );
  }

  private callAction<T = any>(actioncode: number, data: Record<string, any> = {}, skipLoader = false): Observable<T> {
    return this.http
      .post<any>(this.gatewayUrl, { actioncode, data }, { headers: this.gatewayHeaders(skipLoader) })
      .pipe(
        map((res) => {
          if (!res || res.isSuccess === false) {
            const message = res?.message || 'Request failed.';
            // `errors`: the reasons a refusal gives (the AI draft check's).
            const errors = Array.isArray(res?.errors) ? res.errors : undefined;
            throw { message, error: { message, code: res?.code, ...(errors ? { errors } : {}) }, status: 400 };
          }

          // Extract pagination metadata from envelope if present
          const total =
            res.itemsTotal ??
            res.totalItems ??
            res.total ??
            res.total_items ??
            res.total_count ??
            res.count ??
            res.recordsTotal ??
            res.totalRecords ??
            res.pagination?.total ??
            res.pagination?.totalItems ??
            res.meta?.total ??
            res.meta?.totalItems ??
            res.data?.itemsTotal ??
            res.data?.totalItems ??
            res.data?.total ??
            res.data?.total_count ??
            res.data?.count;

          const curPage =
            res.curPage ??
            res.currentPage ??
            res.page ??
            res.pagination?.page ??
            res.pagination?.currentPage ??
            res.meta?.page ??
            res.meta?.currentPage ??
            res.data?.curPage ??
            res.data?.currentPage ??
            res.data?.page;

          const pageTotal =
            res.pageTotal ??
            res.totalPages ??
            res.pagesTotal ??
            res.pagination?.totalPages ??
            res.pagination?.pageTotal ??
            res.meta?.totalPages ??
            res.data?.pageTotal ??
            res.data?.totalPages;

          if (Array.isArray(res.data)) {
            const arr = res.data as any;
            if (total !== undefined) {
              arr.itemsTotal = Number(total);
              arr.totalItems = Number(total);
              arr.total = Number(total);
              arr.total_count = Number(total);
            }
            if (curPage !== undefined) {
              arr.curPage = Number(curPage);
              arr.currentPage = Number(curPage);
            }
            if (pageTotal !== undefined) {
              arr.pageTotal = Number(pageTotal);
              arr.totalPages = Number(pageTotal);
            }
            arr.items = res.data;

            if (total !== undefined || curPage !== undefined || pageTotal !== undefined) {
              return {
                items: res.data,
                data: res.data,
                itemsTotal: total !== undefined ? Number(total) : res.data.length,
                totalItems: total !== undefined ? Number(total) : res.data.length,
                total: total !== undefined ? Number(total) : res.data.length,
                total_count: total !== undefined ? Number(total) : res.data.length,
                curPage: curPage !== undefined ? Number(curPage) : (data?.['page'] || 1),
                currentPage: curPage !== undefined ? Number(curPage) : (data?.['page'] || 1),
                pageTotal: pageTotal !== undefined ? Number(pageTotal) : Math.ceil((total ?? res.data.length) / (data?.['per_page'] || 20)),
                totalPages: pageTotal !== undefined ? Number(pageTotal) : Math.ceil((total ?? res.data.length) / (data?.['per_page'] || 20)),
              } as unknown as T;
            }
            return arr as T;
          }

          if (res.data && typeof res.data === 'object') {
            const dataObj = { ...res.data };
            if (total !== undefined) {
              if (dataObj.itemsTotal === undefined) dataObj.itemsTotal = Number(total);
              if (dataObj.total === undefined) dataObj.total = Number(total);
              if (dataObj.totalItems === undefined) dataObj.totalItems = Number(total);
            }
            if (curPage !== undefined && dataObj.curPage === undefined) dataObj.curPage = Number(curPage);
            if (pageTotal !== undefined && dataObj.pageTotal === undefined) dataObj.pageTotal = Number(pageTotal);
            return dataObj as T;
          }

          return res.data as T;
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  /** action 8218 · admin-contest-ai-generate — the gateway checks the target client's plan (AI on,
   *  budget left) and proxies to the AI generator; `data` is the generated campaign. */
  generateAiContest(payload: any): Observable<any> {
    return this.callAction<any>(8218, payload, true);
  }

  /** action 8295 · admin-contest-ai-suggestions — quick contest ideas for one client. The gateway
   *  checks that client's plan has AI and calls the suggestions model with the server-side key;
   *  `data` is `{ client_id, ideas: [{ title, description }] }`. (8293 is admin-contest-duplicate.) */
  getAiContestSuggestions(clientId: string): Observable<{ client_id: string; ideas: Array<{ title: string; description: string }> }> {
    return this.callAction<any>(8295, { client_id: clientId }, true);
  }

  /** action 8158 · admin-ai-schedule-list. */
  getAiSchedules(params?: any): Observable<any> {
    return this.callAction<any>(8158, params ?? {}, true).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to load schedules.')))
    );
  }

  /** action 8159 · admin-ai-schedule-save (create when body.id is absent, else update). */
  saveAiSchedule(body: any): Observable<any> {
    return this.callAction<any>(8159, body).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to save schedule.')))
    );
  }

  /** action 8160 · admin-ai-schedule-archive. */
  archiveAiSchedule(id: number | string): Observable<any> {
    return this.callAction<any>(8160, { id }).pipe(
      catchError((error) => throwError(() => new Error(error?.message || 'Failed to archive schedule.')))
    );
  }
}
